import { useNavigate } from 'react-router-dom';
import { Lock, ArrowUpRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const TIER_UPGRADE_MAP = {
  pro: { name: 'Teams', color: 'indigo' },
  enterprise: { name: 'Enterprise', color: 'amber' },
};

export const UpgradePrompt = ({ 
  requiredTier = 'pro', 
  feature = 'this feature',
  compact = false,
  className = '' 
}) => {
  const navigate = useNavigate();
  const tierInfo = TIER_UPGRADE_MAP[requiredTier] || TIER_UPGRADE_MAP.pro;

  if (compact) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-lg ${className}`}>
        <Lock className="w-3.5 h-3.5 text-zinc-400" />
        <span className="text-xs text-zinc-400">
          {tierInfo.name} plan required
        </span>
        <button 
          onClick={() => navigate('/billing')}
          className="text-xs text-indigo-400 hover:text-indigo-300 font-medium ml-auto"
          data-testid="upgrade-prompt-compact-btn"
        >
          Upgrade
        </button>
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center justify-center py-12 px-6 ${className}`} data-testid="upgrade-prompt">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-amber-500/20 flex items-center justify-center mb-6">
        <Sparkles className="w-8 h-8 text-indigo-400" />
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">
        Upgrade to {tierInfo.name}
      </h3>
      <p className="text-sm text-zinc-400 text-center max-w-md mb-6">
        Access to {feature} requires a {tierInfo.name} plan or higher. 
        Upgrade now to unlock this and other premium features.
      </p>
      <Button 
        onClick={() => navigate('/billing')}
        className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
        data-testid="upgrade-prompt-btn"
      >
        View Plans <ArrowUpRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const FeatureGate = ({ children, feature, requiredTier = 'pro', canAccess, fallback }) => {
  if (canAccess) return children;
  if (fallback) return fallback;
  return <UpgradePrompt requiredTier={requiredTier} feature={feature} />;
};

export const TierBadge = ({ tier }) => {
  const badges = {
    free: { label: 'Individual', className: 'bg-zinc-700/50 text-zinc-300' },
    basic: { label: 'Individual', className: 'bg-zinc-700/50 text-zinc-300' },
    individual: { label: 'Individual', className: 'bg-zinc-700/50 text-zinc-300' },
    pro: { label: 'Teams', className: 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' },
    enterprise: { label: 'Enterprise', className: 'bg-amber-500/20 text-amber-300 border border-amber-500/30' },
  };

  const badge = badges[tier] || badges.free;

  return (
    <span 
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${badge.className}`}
      data-testid="tier-badge"
    >
      {badge.label}
    </span>
  );
};
